import React, { memo, useCallback, useEffect, useState } from "react";
import Table from "react-bootstrap/Table";
import styled from "@emotion/styled";

import { formatDate, getDateByTimeZone } from "@/lib/date";
import { Button } from "@/components/Buttons";
import Modal from "@/components/Modal";
import {
  Delivery,
  DELIVERY_TYPE_PICKUP,
  DELIVERY_TYPE_NORMAL,
} from "@/Models/Delivery";
import {
  DeliveryStatusLog,
  DeliveryCollectionLog,
  DeliveryPrintLog,
} from "@/Models";

import { WaybillModal } from "@/Modals";

import {
  useAllocationGroupsStore,
  useDelayedDeliveriesStore,
  useDeliveriesStore,
  useLoadingStore,
  useModalStore,
  useUsersStore,
} from "@/store/hooks";

const Td = styled.td`
  width: calc(100% - 200px);
  padding: 0.5rem !important;
`;

const Th = styled.th`
  width: 200px;
  padding: 0.5rem !important;
`;

const Title = styled.h3`
  margin: 0;
`;
const SubTitle = styled.h6`
  margin-top: 10px;
  font-weight: bold;
`;

const FlexSpaceWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogsWrap = styled.div``;
const LogItem = styled.div``;

const ImagesWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const renderBookId = (bookId, type) => {
  switch (type) {
    case DELIVERY_TYPE_PICKUP:
      return `(픽) ${bookId}`;
    default:
      return bookId;
  }
};

// 배송완료 이미지 표시
const DeliveryCompletedImage = memo(({ images }) => {
  const [showImages, setShowImages] = useState(false);

  const handleClickShowImages = useCallback(() => {
    setShowImages(true);
  }, []);

  return (
    <ImagesWrap>
      <div>
        <a href={images[images.length - 1]} target="_blank" rel="noreferrer">
          <img alt="" src={images[images.length - 1]} width="100%" />
        </a>
      </div>
      {showImages && (
        <div>
          {images.slice(0, images.length - 1).map((image) => (
            <a key={image} href={image} target="_blank" rel="noreferrer">
              <img alt="" src={image} width="100%" />
            </a>
          ))}
        </div>
      )}
      {!showImages && images.length > 1 && (
        <Button type="button" onClick={handleClickShowImages}>
          이전 완료 이미지
        </Button>
      )}
    </ImagesWrap>
  );
});

const DeliveryForRiderModal = ({
  bookId,
  hanjinOnly = false,
  defaultSizeType,
  ...props
}) => {
  const { allocationGroupsCache } = useAllocationGroupsStore().state;
  const { ...allocationGroupsActions } = useAllocationGroupsStore();
  const { openModal, closeModal } = useModalStore();
  const { ...delayedDeliveriesActions } = useDelayedDeliveriesStore();
  const { ...actions } = useDeliveriesStore();
  const { startLoading, finishLoading } = useLoadingStore();
  const { usersCache } = useUsersStore().state;
  const { ...usersActions } = useUsersStore();

  const [fetchedDeliveryStatusLogs, setFetchedDeliveryStatusLogs] =
    useState(false);
  const [fetchedDeliveryCollectionLogs, setFetchedDeliveryCollectionLogs] =
    useState(false);
  const [fetchedDeliveryPrintLogs, setFetchedDeliveryPrintLogs] =
    useState(false);

  useEffect(() => {
    fetchById();
  }, []);

  const handleClickPrint = useCallback(
    (deliveries) => {
      openModal(
        <WaybillModal
          key="waybill-modal"
          deliveries={deliveries}
          defaultSizeType={defaultSizeType}
          hanjinOnly={hanjinOnly}
          onAfterSubmit={() => {}}
        />,
      );
    },
    [defaultSizeType, hanjinOnly],
  );

  const LogRows = ({ row }) => {
    return (
      <LogItem>
        {row.isAgency ? "(배)" : ""} {row.renderCreatedAt()}{" "}
        {row.renderLogStatus()}
        {row.isAgency
          ? ""
          : `(${row.renderStatusBefore()}→${row.renderStatusAfter()})`}{" "}
        {row.logWriter}
        {row.agencyDeliveryRiderName
          ? `| 배달지정배송원명: ${row.agencyDeliveryRiderName}`
          : ""}
        {row.agencyFailedReason ? `| 실패사유: ${row.agencyFailedReason}` : ""}
      </LogItem>
    );
  };

  const CollectionLogRows = ({ row }) => {
    return (
      <LogItem>
        {row.renderCreatedAt()} {row.renderCollectionType()} {row.logWriter}
      </LogItem>
    );
  };

  const PrintLogRows = ({ row }) => {
    return (
      <LogItem>
        {row.renderCreatedAt()} {row.renderPlatform()} {row.logWriter}
      </LogItem>
    );
  };

  const [rowsDefault, setRowsDefault] = useState([]);
  const [rowsSender, setRowsSender] = useState([]);
  const [rowsReceiver, setRowsReceiver] = useState([]);
  const [rowsProduct, setRowsProduct] = useState([]);
  const [rowsTracking, setRowsTracking] = useState([]);
  const [rowsLog, setRowsLog] = useState([]);
  const [rowsCollectionLog, setRowsCollectionLog] = useState([]);
  const [rowsPrintLog, setRowsPrintLog] = useState([]);
  const [senderAllocationGroup, setSenderAllocationGroup] = useState(null);
  const [receiverAllocationGroup, setReceiverAllocationGroup] = useState(null);

  const [collectionLogWithCs, setCollectionLogWithCs] = useState(null);
  const [collectionLogWithoutCs, setCollectionLogWithoutCs] = useState(null);

  const fetchById = useCallback(async () => {
    try {
      const _delivery = await actions.fetchByIdOnTheFly(bookId);
      const delayedDeliveries =
        await delayedDeliveriesActions.fetchLatestOneByBookId(bookId);

      const delivery = new Delivery(_delivery);

      fetchAllocationGroup(delivery);

      let _rowsDefault = [
        {
          header: "예약번호 (배송상태)",
          data: (
            <FlexSpaceWrap>
              <div>
                {renderBookId(delivery.bookId, delivery.type)} (
                {delivery.renderStatus()}
                {delivery.cjOrderIdFromCorpFromOliveyoung
                  ? ", CJ대한통운 배송"
                  : ""}
                )
                {delivery.deliveryRiderName
                  ? ` [${delivery.deliveryRiderName}${
                      delivery.orderingNumberByDeliveryRiderId
                        ? ` ${delivery.orderingNumberByDeliveryRiderId}`
                        : ""
                    }]`
                  : ""}
              </div>
              <Button
                onClick={() => handleClickPrint([_delivery])}
                variant="secondary"
              >
                운송장 다운로드
              </Button>
            </FlexSpaceWrap>
          ),
        },
        {
          header: "사측 주문번호",
          data: delivery.orderIdFromCorp,
        },
        {
          header: "사측 주문번호2",
          data: delivery.orderIdFromCorpSub,
        },
        {
          header: "접수일시",
          data: `${formatDate(
            getDateByTimeZone(delivery.receiptDate),
            "MM/dd HH:mm",
          )}${
            delivery.requestedDeliveryTime
              ? ` (배송요청일시: ${delivery.renderRequestedDeliveryTime()})`
              : ""
          }`,
        },
        {
          header: "기업회원",
          data: delivery.corpUserCorpTitle,
        },
        {
          header: "기업회원 연락처",
          data: delivery.corpUserContact,
        },
        {
          header: "접수점 주소",
          data: delivery.spot ? delivery.spot.address : "",
        },
      ];

      let _rowsSender = [
        {
          header: "송하인명",
          data: delivery.senderName,
        },
        {
          header: "송하인 연락처",
          data: delivery.senderMobile2,
        },
        {
          header: "송하인 연락처2",
          data: delivery.senderMobile2,
        },
        {
          header: "송하인 우편번호",
          data: delivery.senderPostalCode || delivery.senderAddressPostalCode,
        },
        {
          header: "송하인 주소(지번)",
          data: delivery.senderAddress,
        },
        {
          header: "송하인 주소(도로명)",
          data: delivery.senderAddressRoad,
        },
        {
          header: "송하인 상세주소",
          data: delivery.senderAddressDetail,
        },
        {
          header: "위치(위도, 경도)",
          data: `${delivery.senderLnglat.latitude}, ${delivery.senderLnglat.longitude}`,
        },
      ];

      let _rowsReceiver = [
        {
          header: "수하인명",
          data: delivery.receiverName,
        },
        {
          header: "수하인 연락처",
          data: delivery.receiverMobile,
        },
        {
          header: "수하인 연락처2",
          data: delivery.receiverMobile2,
        },
        {
          header: "수하인 우편번호",
          data: delivery.receiverAddressPostalCode,
        },
        {
          header: "수하인 주소(지번)",
          data: delivery.receiverAddress,
        },
        {
          header: "수하인 주소(도로명)",
          data: delivery.receiverAddressRoad,
        },
        {
          header: "수하인 아파트동",
          data: delivery.receiverBuildingDongNumber || "없음",
        },
        {
          header: "수하인 상세주소",
          data: delivery.receiverAddressDetail,
        },
        {
          header: "위치(위도, 경도)",
          data: `${delivery.receiverLnglat.latitude}, ${delivery.receiverLnglat.longitude}`,
        },
        {
          header: "주소정제 API",
          data: delivery.renderAddressApiType,
        },
        {
          header: "고객입력 우편번호",
          data: delivery.customerAddressPostalCode,
        },
        {
          header: "고객 입력 주소",
          data:
            delivery.unrefinedAddress ||
            `${delivery.customerAddress || ""} ${
              delivery.customerAddressDetail || ""
            }`,
        },
      ];

      let _rowsProduct = [
        {
          header: "물품명",
          data: delivery.totalProductName,
        },
        {
          header: "물품 가격",
          data:
            new Intl.NumberFormat({
              style: "currency",
              currency: "KRW",
            }).format(delivery.productPrice) + "원",
        },
        {
          header: "수령희망장소",
          data: delivery.deliveryPlace
            ? `(${formatDate(
                getDateByTimeZone(delivery.deliveryPlacedTime || new Date()),
                "HH:mm",
              )})${delivery.deliveryPlace}`
            : "",
        },
      ];
      let _rowsTracking = [
        {
          header: "취소여부",
          data: delivery.isCanceled ? "O" : "X",
        },
        {
          header: "취소날짜",
          data: delivery.renderCanceledAt(),
        },
        {
          header: "취소사유",
          data: delivery.canceledReason,
        },
        {
          header: "사고여부",
          data: delivery.isAccident ? "O" : "X",
        },
        {
          header: "사고날짜",
          data: delivery.renderAccidentAt(),
        },
        {
          header: "사고사유",
          data: delivery.accidentReason,
        },
        {
          header: "기타사항1",
          data: delivery.etc1,
        },
        {
          header: "기타사항2",
          data: delivery.etc2,
        },
        {
          header: "기타사항3",
          data: delivery.etc3,
        },
        {
          header: `수취인 서명 이미지${
            delivery.signImageLocations.length +
              delivery.notReceivedImageLocations.length >
            1
              ? `(정정배송)`
              : ""
          }`,
          data: <DeliveryCompletedImage images={delivery.signImageLocations} />,
        },
        {
          header: `미수취 촬영 이미지${
            delivery.signImageLocations.length +
              delivery.notReceivedImageLocations.length >
            1
              ? `(정정배송)`
              : ""
          }`,
          data: (
            <DeliveryCompletedImage
              images={delivery.notReceivedImageLocations}
            />
          ),
        },
        {
          header: "배완사유",
          data: delivery.renderManualCompletedDeliveryReason,
        },
        {
          header: "미배송",
          data: delayedDeliveries.length
            ? `사유: ${delayedDeliveries[0].reason}\n일시: ${formatDate(
                new Date(delayedDeliveries[0].createdAt),
                "yyyy-MM-dd HH:mm",
              )}`
            : delivery.renderDelayReasonCode(),
        },
        {
          header: "반송완료",
          data: delivery.sentBackLogs.length
            ? delivery.sentBackLogs
                .map(
                  (dd) =>
                    `${formatDate(
                      getDateByTimeZone(dd.createdAt),
                      "MM/dd HH:mm",
                    )} : ${dd.reason}`,
                )
                .join("\n")
            : "",
        },
        {
          header: "분실완료",
          data: `${delivery.renderLostDate() || ""} ${delivery.lostReason}`,
        },
      ];

      if (delivery.pickupCompletedImage) {
        _rowsTracking.push({
          header: "반품수거 촬영 이미지",
          data: delivery.pickupCompletedImage && (
            <a
              href={delivery.pickupCompletedImage}
              target="_blank"
              rel="noreferrer"
            >
              <img alt="" src={delivery.pickupCompletedImage} width="100%" />
            </a>
          ),
        });
      }

      if (delivery.deliveryDoneLatitude && delivery.deliveryDoneLongitude) {
        _rowsTracking.push({
          header: "배송완료 위치(위경도)",
          data: `${delivery.deliveryDoneLatitude}, ${delivery.deliveryDoneLongitude}`,
        });
      }

      if (delivery.completedLocation) {
        _rowsTracking.push({
          header: "배송완료 위치(장소) 및 안내 문자발송 여부",
          data: `${delivery.completedLocation} / ${
            delivery.sendedCompletedSms ? "발송완료" : "미발송"
          }`,
        });
      }

      if (
        delivery.deliverySentBackLatitude &&
        delivery.deliverySentBackLongitude
      ) {
        _rowsTracking.push({
          header: "반송완료 위치(위경도)",
          data: `${delivery.deliverySentBackLatitude}, ${delivery.deliverySentBackLongitude}`,
        });
      }

      if (delivery.deliveryLostLatitude && delivery.deliveryLostLongitude) {
        _rowsTracking.push({
          header: "분실완료 위치(위경도)",
          data: `${delivery.deliveryLostLatitude}, ${delivery.deliveryLostLongitude}`,
        });
      }

      if (delivery.agencyOrderId) {
        _rowsTracking.push({
          header: "QDN주문_일련번호",
          data: delivery.agencyOrderId,
        });

        _rowsTracking.push({
          header: "배송원성명(배송원연락처)",
          data: `${delivery.agencyDeliveryRiderName || ""}(${
            delivery.agencyDeliveryRiderMobile || ""
          })`,
        });
      }

      if (delivery.agencyNotReceivedImageLocation) {
        _rowsTracking.push({
          header: "기타 이미지",
          data: (
            <a
              href={delivery.agencyNotReceivedImageLocation}
              target="_blank"
              rel="noreferrer"
            >
              <img
                alt=""
                src={delivery.agencyNotReceivedImageLocation}
                width="100%"
              />
            </a>
          ),
        });
      }

      setRowsDefault(_rowsDefault);
      setRowsSender(_rowsSender);
      setRowsReceiver(_rowsReceiver);
      setRowsProduct(_rowsProduct);
      setRowsTracking(_rowsTracking);
    } catch (e) {
      window.alert(e.message);
      closeModal();
    }
  });

  const fetchAllocationGroup = useCallback(async (delivery) => {
    try {
      let _allocationGroup = null;

      if (delivery.type === DELIVERY_TYPE_NORMAL) {
        if (delivery.receiverPostalCode) {
          const _allocationGroupCache = allocationGroupsCache.find(
            (c) =>
              c.postalCode === delivery.receiverPostalCode &&
              c.corpUserId === delivery.corpUser?.id,
          );
          if (!_allocationGroupCache) {
            _allocationGroup = await allocationGroupsActions.fetchByPostalCode({
              postalCode: delivery.receiverPostalCode,
              corpUserId: delivery.corpUser?.id,
            });
          } else {
            _allocationGroup = _allocationGroupCache;
          }

          setReceiverAllocationGroup(_allocationGroup);
        }
      } else {
        if (delivery.senderPostalCode) {
          const _allocationGroupCache = allocationGroupsCache.find(
            (c) =>
              c.postalCode === delivery.senderPostalCode &&
              c.corpUserId === delivery.corpUser?.id,
          );
          if (!_allocationGroupCache) {
            _allocationGroup = await allocationGroupsActions.fetchByPostalCode({
              postalCode: delivery.senderPostalCode,
              corpUserId: delivery.corpUser?.id,
            });
          } else {
            _allocationGroup = _allocationGroupCache;
          }

          setSenderAllocationGroup(_allocationGroup);
        }
      }
    } catch (e) {}
  }, []);

  const fetchDeliveryStatusLogs = useCallback(async () => {
    try {
      startLoading();
      const { rows } = await actions.fetchDeliveryStatusLogs(bookId);

      const _deliveryStatusLogs = rows?.sort((a, b) => {
        return a.createdAt < b.createdAt ? -1 : 1;
      });

      setRowsLog(
        _deliveryStatusLogs?.map((d, index) => new DeliveryStatusLog(d, index)),
      );
      setFetchedDeliveryStatusLogs(true);
    } catch (e) {
      window.alert(`배송 로그를 불러오는데 실패했습니다. ${e.message}`);
    } finally {
      finishLoading();
    }
  }, []);

  const fetchDeliveryCollectionLogs = useCallback(async () => {
    try {
      const { rows } = await actions.fetchDeliveryCollectionLogs(bookId);

      const deliveryCollectionLogs = rows
        ?.sort((a, b) => {
          return a.createdAt < b.createdAt ? 1 : -1;
        })
        ?.map((d) => new DeliveryCollectionLog(d));

      if (deliveryCollectionLogs.length) {
        const _collectionLogWithCs = deliveryCollectionLogs.find(
          (d) => !!d.etc?.withCs,
        );
        const _collectionLogWithoutCs = deliveryCollectionLogs.find(
          (d) => !d.etc?.withCs,
        );

        setCollectionLogWithCs(_collectionLogWithCs);
        setCollectionLogWithoutCs(_collectionLogWithoutCs);
      }

      setRowsCollectionLog(deliveryCollectionLogs);

      setFetchedDeliveryCollectionLogs(true);
    } catch (e) {
      window.alert(`회수 로그를 불러오는데 실패했습니다. ${e.message}`);
    }
  }, [bookId]);

  const fetchDeliveryPrintLogs = useCallback(async () => {
    try {
      const { rows } = await actions.fetchDeliveryPrintLogs(bookId);

      const _printLogs = [...rows];

      await Promise.all(
        _printLogs.map(async (log) => {
          if (log.platform === "admin") {
            const writer = usersCache.find((user) => user.id === log.userId);

            if (writer) {
              log.writer = writer;
            } else {
              const fetchedWriter = await usersActions.fetchByIdOnTheFly(
                log.userId,
              );

              log.writer = fetchedWriter;
            }
          }
        }),
      );

      setRowsPrintLog(_printLogs?.map((d) => new DeliveryPrintLog(d)));
      setFetchedDeliveryPrintLogs(true);
    } catch (e) {
      window.alert(`운송장 출력 로그를 불러오는데 실패했습니다. ${e.message}`);
    }
  }, [bookId]);

  const TableRows = ({ row }) => {
    return (
      <tr>
        <Th>{row.header}</Th>
        <Td>
          {typeof row.data === "string"
            ? row.data.split("\n").map((line, idx) => {
                return (
                  <span key={idx}>
                    {line}
                    <br />
                  </span>
                );
              })
            : row.data}
        </Td>
      </tr>
    );
  };

  return (
    <Modal size="lg" {...props}>
      <Modal.Header>
        <Title>배송 정보</Title>
      </Modal.Header>
      <Modal.Body>
        <SubTitle>기본정보</SubTitle>
        <Table bordered>
          <tbody>
            {rowsDefault.map((row, i) => (
              <TableRows row={row} key={i} />
            ))}
          </tbody>
        </Table>
        <SubTitle>송하인 정보</SubTitle>
        <Table bordered>
          <tbody>
            {rowsSender.map((row, i) => (
              <TableRows row={row} key={i} />
            ))}
            {senderAllocationGroup && (
              <TableRows
                row={{
                  header: "지역그룹/동",
                  data: `${senderAllocationGroup?.name || ""}`,
                }}
              />
            )}
          </tbody>
        </Table>
        <SubTitle>수하인 정보</SubTitle>
        <Table bordered>
          <tbody>
            {rowsReceiver.map((row, i) => (
              <TableRows row={row} key={i} />
            ))}
            {receiverAllocationGroup && (
              <TableRows
                row={{
                  header: "지역그룹/동",
                  data: `${receiverAllocationGroup?.name || ""}`,
                }}
              />
            )}
          </tbody>
        </Table>
        <SubTitle>물품정보</SubTitle>
        <Table bordered>
          <tbody>
            {rowsProduct.map((row, i) => (
              <TableRows row={row} key={i} />
            ))}
          </tbody>
        </Table>
        <SubTitle>배송정보</SubTitle>
        <Table bordered>
          <tbody>
            {rowsTracking.map((row, i) => (
              <TableRows row={row} key={i} />
            ))}
            {collectionLogWithCs && (
              <tr>
                <Th>CS회수촬영이미지</Th>
                <Td>
                  <a
                    href={collectionLogWithCs?.imagePath}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      alt=""
                      src={collectionLogWithCs?.imagePath}
                      width="100%"
                    />
                  </a>
                </Td>
              </tr>
            )}
            {collectionLogWithoutCs && (
              <tr>
                <Th>회수촬영이미지</Th>
                <Td>
                  <a
                    href={collectionLogWithoutCs?.imagePath}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      alt=""
                      src={collectionLogWithoutCs?.imagePath}
                      width="100%"
                    />
                  </a>
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
        <SubTitle>배송로그</SubTitle>
        <LogsWrap>
          {fetchedDeliveryStatusLogs ? (
            <>
              {rowsLog.map((row, i) => (
                <LogRows row={row} key={i} />
              ))}
            </>
          ) : (
            <Button onClick={fetchDeliveryStatusLogs}>배송 로그 조회</Button>
          )}
        </LogsWrap>
        <SubTitle>회수로그</SubTitle>
        <LogsWrap>
          {fetchedDeliveryCollectionLogs ? (
            <>
              {rowsCollectionLog.map((row, i) => (
                <CollectionLogRows row={row} key={i} />
              ))}
            </>
          ) : (
            <Button onClick={fetchDeliveryCollectionLogs}>
              회수 로그 조회
            </Button>
          )}
        </LogsWrap>
        <SubTitle>운송장 출력 로그</SubTitle>
        <LogsWrap>
          {fetchedDeliveryPrintLogs ? (
            <>
              {rowsPrintLog.map((row, i) => (
                <PrintLogRows row={row} key={i} />
              ))}
            </>
          ) : (
            <Button onClick={fetchDeliveryPrintLogs}>
              운송장 출력 로그 조회
            </Button>
          )}
        </LogsWrap>
      </Modal.Body>
    </Modal>
  );
};

export default DeliveryForRiderModal;
